import { getFirebaseApp } from './utils/firebase.js';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber, signInAnonymously, onAuthStateChanged, GoogleAuthProvider, signInWithCredential, signInWithPopup, signOut } from 'firebase/auth';
import Analytics from './analytics.js';
import currentTime from './utils/time.js';
import get from 'lodash.get';

const REDIRECT_KEY = "auth::redirect";
const TRUST_MET_KEY = "auth::meetsTrustThreashold";
const SIGN_OUT_TIME_KEY = "auth::signOutTime";

class Auth {
  constructor () {
    this.trustTimerStarted = false;
    const firebaseApp = getFirebaseApp();
    this.auth = getAuth(firebaseApp);
    this.vue = null;
    this.router = null;
    this.store = null;
    this.session = null;
    this.dialog = null;
    this.analytics = JSON.parse(import.meta.env.VITE_APP_ENABLE_ANALYTICS) ? Analytics.get() : null;
    this.gisLoaded = false;
    this.googleClientId = import.meta.env.VITE_APP_GOOGLE_OAUTH_CLIENT_ID;
    this.recaptchaVerifier = null;
    this.confirmationResult = null;
  }

  configurePhoneAuth (containerOrId, size = "invisible") {
    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, containerOrId, { size });
  }

  async sendPhoneVerificationCode (phoneNumber) {
    if (!this.recaptchaVerifier) {
      this.configurePhoneAuth("recaptcha-container");
    }
    this.confirmationResult = await signInWithPhoneNumber(this.auth, phoneNumber, this.recaptchaVerifier);
    return this.confirmationResult;
  }

  async verifyPhoneCode (code) {
    if (!this.confirmationResult) throw new Error("No confirmation result. Send a verification code first.");
    const result = await this.confirmationResult.confirm(code);
    const isAnonymous = await this.signIn(result.user, result);
    this.redirect(isAnonymous);
  }

  // Used for e2e testing on staging.
  signInAnonymously () {
    return signInAnonymously(this.auth);
  }

  configure (router, store, session, dialog, _providers) {
    this.router = router;
    this.store = store;
    this.session = session;
    this.dialog = dialog;
    this.providers = _providers || [];
  }

  hasGoogleProvider () {
    return this.providers.includes("google");
  }

  hasAnonymousProvider () {
    return this.providers.some(p => p === "anonymous" || p?.provider === "anonymous");
  }

  isGuestSignInHidden () {
    const provider = this.providers.find(p => p?.provider === "anonymous");
    return provider?.hidden === true;
  }

  revealGuestSignIn () {
    if (!this.isGuestSignInHidden()) return;
    this.guestSignInClicks = (this.guestSignInClicks || 0) + 1;
    if (this.guestSignInClicks >= 5) {
      this.store.setGuestSignInRevealed();
    }
  }

  onReady (createVue) {
    onAuthStateChanged(this.auth, (authUser) => {
      if (this.vue) {
        if (authUser && authUser.isAnonymous) {
          this.signIn(authUser).then(isAnonymous => {
            this.redirect(isAnonymous);
          });
        }
      } else {
        if (authUser) {
          this.signIn(authUser, {}, false).then(() => {
            this.vue = createVue();
          });
        } else {
          this.vue = createVue();
        }
      }
    });
  }

  async loadGis () {
    if (!this.googleClientId || this.gisLoaded) return;
    if (!window.google?.accounts) {
      await new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.onload = resolve;
        document.head.appendChild(script);
      });
    }
    google.accounts.id.initialize({
      client_id: this.googleClientId,
      callback: (response) => {
        const credential = GoogleAuthProvider.credential(response.credential);
        signInWithCredential(this.auth, credential).then((result) => {
          this.signIn(result.user, result).then(isAnonymous => {
            this.redirect(isAnonymous);
          });
        });
      },
      auto_select: true,
      cancel_on_tap_outside: true
    });
    this.gisLoaded = true;
  }

  async signInWithGoogle () {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(this.auth, provider);
    const isAnonymous = await this.signIn(result.user, result);
    this.redirect(isAnonymous);
  }

  bodyClicked () {
    if (!this.googleClientId) return;
    if (!this.trustTimerStarted && !this.trustMet()) {
      this.trustTimerStarted = true;
      setTimeout(async () => {
        this.set(TRUST_MET_KEY, true);
        if (this.longEnoughSinceSignOut() && !this.auth.currentUser) {
          await this.loadGis();
          google.accounts.id.prompt();
        }
      }, 8000);
    }
  }

  set (key, item) {
    localStorage.setItem(key, JSON.stringify(item));
  }

  get (key) {
    return JSON.parse(localStorage.getItem(key));
  }

  remove (key) {
    localStorage.removeItem(key);
  }

  signIn (authUser, authResult = {}, showSnackbar = true) {
    const userProps = (({ displayName, photoURL, isAnonymous }) => ({ displayName, photoURL, isAnonymous }))(authUser);
    userProps.signInMethod = get(authResult, "credential.signInMethod", get(authUser, "providerData[0].providerId", "anonymous"));
    userProps.lastLoggedInAt = currentTime();
    if (userProps.isAnonymous) {
      userProps.betaTester = true;
    }
    return this.session.findOrCreateUserById(authUser.uid, userProps).then((user) => {
      if (this.analytics) { this.analytics.login(user, authUser.email, userProps.signInMethod); }
      user.findOrCreatePrivateProfile().then(privateProfile => {
        privateProfile.immediateUpdate({ email: authUser.email, phoneNumber: authUser.phoneNumber });
      });
      this.store.setUser(user);
      if (showSnackbar) {
        this.store.pushSnackbarMessageProps({ message: "Sign-in successful." });
      }
      return userProps.isAnonymous;
    });
  }

  signOut () {
    let goHome = Promise.resolve();
    if (this.router.currentRoute.value.name !== "Home") {
      goHome = this.router.push({ name: "Home" });
    }
    return goHome.then(() => {
      return this.store.user
        ? this.store.user.unsubscribe().then(() => {
          if (this.analytics) { this.analytics.logout(); }
          this.set(SIGN_OUT_TIME_KEY, Date.now());
          if (window.google?.accounts?.id) {
            google.accounts.id.cancel();
          }
          return signOut(this.auth).then((result) => {
            this.session.users = {};
            this.store.signOut();
            return result;
          });
        })
        : Promise.resolve();
    });
  }

  isSigningInBeforeRedirect () {
    return (Object.keys(this.redirectRoute || {}).length > 0);
  }

  redirect (isAnonymous) {
    if (!this.redirectRoute) {
      this.redirectRoute = this.get(REDIRECT_KEY);
    }
    if (this.redirectRoute) {
      this.remove(REDIRECT_KEY);
      this.router.replace(this.redirectRoute)
        .catch((e) => {
          if (!e.message.match(/navigation guard/)) {
            throw e;
          }
        });
    }
    this.redirectRoute = null;
  }

  trustMet () {
    return this.get(TRUST_MET_KEY) || false;
  }

  longEnoughSinceSignOut () {
    const oneDay = (60 * 24);
    return (this.numMinutesSinceSignOut() > oneDay);
  }

  openDialog (redirect = {}, header = "sign-in-and-save-to-the-cloud", paramName = "dialog") {
    if (Object.keys(redirect).length > 0) {
      this.set(REDIRECT_KEY, redirect);
    }
    this.dialog.push(header, undefined, paramName);
  }

  numMinutesSinceSignOut () {
    const signOutTime = this.get(SIGN_OUT_TIME_KEY) || 0;
    return (((Date.now() - signOutTime) / 1000) / 60);
  }
}

var Auth$1 = Auth;

export { Auth$1 as default };
